import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const BrandContext = createContext(null);

function hexToHsl(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      case b: hue = (r - g) / d + 4; break;
      default: break;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export const BrandProvider = ({ children }) => {
  const [brand, setBrand] = useState(null);

  const apply = useCallback((b) => {
    if (!b) return;
    const root = document.documentElement;
    if (b.primary_color) {
      root.style.setProperty("--primary", b.primary_color);
      root.style.setProperty("--primary-hsl", hexToHsl(b.primary_color));
    }
    if (b.accent_color) root.style.setProperty("--accent-color", b.accent_color);
    if (b.brand_name) document.title = `${b.brand_name} — ${b.tagline || "Assistant commercial IA"}`;
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setBrand(data);
      apply(data);
    }).catch(() => {});
  }, [apply]);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/settings");
    setBrand(data);
    apply(data);
  }, [apply]);

  return <BrandContext.Provider value={{ brand, refresh }}>{children}</BrandContext.Provider>;
};

export const useBrand = () => useContext(BrandContext);
